/**
 * Matrix report charts: X-axis = row or column group, Stack By = other dimension, Y-axis = measure.
 */

import { isMatrixMode, buildMeasuresFromSummarized } from './matrixReportConfig';
import { buildMatrixGrid } from './matrixPivot';
import { buildDateGroupOptions, getReportGroupBucket } from './matrixDateGrouping';
import {
  calculateReportAggregation,
  getAggregationNumericResult,
  formatAggregatedDisplayForReport,
} from '../../../utils/reportCurrencyAggregate';
import {
  getCategoryAxisColorsForChart,
  sortReportGroupKeys,
} from '../charts/chartPicklistColors';

const TOTAL_COLUMN_LABELS = new Set(['total', 'row total', 'grand total']);

/** Category keys excluded from chart axes (grand total buckets). */
const CHART_EXCLUDED_CATEGORY_KEYS = new Set([
  '__all__',
  '__ALL__',
  'Grand Total',
  'grand total',
]);

export const MATRIX_CHART_TOTAL_COLOR_KEY = 'Total';
export const MATRIX_CHART_TOTAL_DEFAULT_COLOR = '#64748b';

const DEFAULT_COLORS = [
  '#36A2EB',
  '#FF6384',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#C9CBCF',
  '#4BC0C0',
];

function isTotalColumn(col) {
  const label = String(col?.label ?? '').trim().toLowerCase();
  return TOTAL_COLUMN_LABELS.has(label);
}

/** When true, matrix charts include a visible Total stack segment; tooltips always show total. */
export function chartShowsMatrixColumnTotal(chart) {
  if (!chart) return false;
  if (chart.showMatrixColumnTotal === true) return true;
  if (chart.show_matrix_column_total === true) return true;
  return false;
}

function parseNumeric(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (raw == null) return 0;
  const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function groupFieldsFromMatrixMetadata(summarizedData) {
  const gf = summarizedData?._metadata?.group_fields || {};
  const rowGroups = [];
  const columnGroups = [];
  Object.entries(gf).forEach(([fieldName, info]) => {
    const entry = { ...info, field_name: fieldName };
    if (info.group_axis === 'column') columnGroups.push(entry);
    else rowGroups.push(entry);
  });
  return { rowGroups, columnGroups };
}

export function isMatrixChart(chart, summarizedData) {
  if (chart?.matrixChart === true || chart?.matrix_chart === true) return true;
  return summarizedData?._metadata?.report_info?.report_type === 'matrix';
}

export function hasStackByDimension(chart) {
  return Boolean(chart?.stackByField || chart?.stack_by_field);
}

/** Summary report with 2+ row groups and Stack By (not matrix column groups). */
export function isSummaryStackedChart(chart, summarizedData, selectedGroups = []) {
  if (!hasStackByDimension(chart)) return false;
  if (isMatrixChart(chart, summarizedData)) return false;
  const groupCount =
    summarizedData?._metadata?.report_info?.group_count ??
    (Array.isArray(selectedGroups) ? selectedGroups.length : 0);
  return groupCount >= 2;
}

/**
 * Metadata-only payload for matrix reports (measures + group dimensions).
 */
export function buildSummarizedDataFromMatrix({
  previewData = [],
  rowGroups = [],
  columnGroups = [],
  selectedFields = [],
  summarizedFields = {},
}) {
  if (!isMatrixMode(columnGroups) || !previewData?.length) {
    return {};
  }

  const measures = buildMeasuresFromSummarized(selectedFields, summarizedFields);

  const summarizedData = {
    _metadata: {
      group_fields: {},
      summarized_fields: {},
      report_info: {
        report_type: 'matrix',
        row_group_count: rowGroups.length,
        column_group_count: columnGroups.length,
        extraction_timestamp: new Date().toISOString(),
        data_structure_version: 'matrix-2.0',
      },
    },
  };

  rowGroups.forEach((f) => {
    summarizedData._metadata.group_fields[f.field_name] = {
      field_id: f.id,
      field_name: f.field_name,
      field_label: f.field_label,
      data_type: f.data_type,
      object_name: f.object_name,
      group_axis: 'row',
      picklist_values: f.picklist_values,
    };
  });

  columnGroups.forEach((f) => {
    summarizedData._metadata.group_fields[f.field_name] = {
      field_id: f.id,
      field_name: f.field_name,
      field_label: f.field_label,
      data_type: f.data_type,
      object_name: f.object_name,
      group_axis: 'column',
      picklist_values: f.picklist_values,
    };
  });

  measures.forEach((m) => {
    summarizedData._metadata.summarized_fields[m.field_name] = {
      field_id: m.id,
      field_name: m.field_name,
      field_label: m.field_label,
      data_type: m.data_type,
      object_name: m.object_name,
      aggregation: m.aggregation,
    };
  });

  return summarizedData;
}

export function isChartGrandTotalCategory(label) {
  const key = String(label ?? '').trim();
  if (!key) return false;
  if (CHART_EXCLUDED_CATEGORY_KEYS.has(key)) return true;
  return key.toLowerCase() === '__all__';
}

/** Remove grand-total rows from chart categories and align series data. */
export function filterChartGrandTotalCategories(extracted) {
  if (!extracted?.labels?.length) return extracted;
  const keepIndexes = [];
  const labels = (extracted.labels || []).filter((label, idx) => {
    if (isChartGrandTotalCategory(label)) return false;
    keepIndexes.push(idx);
    return true;
  });
  if (keepIndexes.length === extracted.labels.length) return extracted;
  const series = (extracted.series || []).map((s) => ({
    ...s,
    data: keepIndexes.map((i) => s.data[i]),
    perCategoryColors: s.perCategoryColors
      ? keepIndexes.map((i) => s.perCategoryColors[i])
      : null,
  }));
  return { ...extracted, labels, series };
}

export function resolveMatrixBarMeasureField(chart, summarizedData, measures) {
  const requested =
    chart?.yAxisField ||
    chart?.yAxisFields?.[0] ||
    chart?.matrixMeasureField ||
    measures[0]?.field_name;
  if (!requested) return 'record_count';
  const meta = summarizedData?._metadata?.summarized_fields?.[requested];
  if (requested === 'record_count') return 'record_count';
  if (meta && !meta.is_derived) return requested;
  if (meta?.is_derived) return 'record_count';
  if (
    measures.some(
      (m) => m.field_name === requested || String(m.id) === String(requested)
    )
  ) {
    return requested;
  }
  return measures[0]?.field_name || 'record_count';
}

export function resolveMatrixLineField(chart, summarizedData) {
  const explicit = chart?.matrixLineField || chart?.matrix_line_field;
  if (explicit) return explicit;
  const yField =
    chart?.yAxisField || chart?.yAxisFields?.[0] || chart?.matrixMeasureField;
  const summarizedFields = summarizedData?._metadata?.summarized_fields || {};
  const yMeta = yField ? summarizedFields[yField] : null;
  if (yMeta?.is_derived) return yField;

  const stackBy = chart?.stackByField || chart?.stack_by_field;
  if ((chart?.matrixChart || stackBy) && Object.keys(summarizedFields).length > 0) {
    const barField = chart?.matrixMeasureField || yField;
    const derivedEntry = Object.entries(summarizedFields).find(
      ([id, m]) =>
        m?.is_derived &&
        id !== barField &&
        (m.data_type === 'percent' ||
          m.derived_type === 'win_rate' ||
          /win\s*rate|lost\s*rate|%/i.test(m.field_label || ''))
    );
    if (derivedEntry) return derivedEntry[0];
  }
  return null;
}

function resolveGroupCategoryEntry(groupData, label) {
  if (!groupData || label == null) return null;
  if (groupData[label]) return groupData[label];
  const target = String(label).trim().toLowerCase();
  const key = Object.keys(groupData).find(
    (k) => String(k).trim().toLowerCase() === target
  );
  return key ? groupData[key] : null;
}

/**
 * Append a line series (e.g. Win Rate %) aligned to matrix category labels.
 */
export function appendMatrixLineOverlay({ extracted, chart, summarizedData, lineField }) {
  if (!extracted?.labels?.length || !lineField || !summarizedData) return extracted;

  const categoryField =
    chart?.xAxisField || chart?.selectedCategoryField || extracted.selectedCategoryField;
  const groupData = summarizedData[categoryField];
  if (!groupData) return extracted;

  const meta = summarizedData._metadata?.summarized_fields?.[lineField];
  const data = extracted.labels.map((label) => {
    const summary = resolveGroupCategoryEntry(groupData, label);
    if (summary?.[lineField]) {
      return parseNumeric(summary[lineField].value);
    }
    if (summarizedData[lineField]?.[label] !== undefined) {
      return parseNumeric(summarizedData[lineField][label]);
    }
    return 0;
  });

  const lineColor =
    chart?.fieldColors?.[lineField] ||
    chart?.legendColors?.[lineField] ||
    '#FF6384';

  const lineSeries = {
    fieldName: lineField,
    name: meta?.field_label || lineField,
    data,
    color: lineColor,
    perCategoryColors: null,
    seriesType: 'line',
    dataType: meta?.data_type || 'percent',
    derivedType: meta?.derived_type,
    isGrowthPercent:
      meta?.derived_type === 'period_over_period' ||
      /growth/i.test(meta?.field_label || ''),
  };

  return {
    ...extracted,
    series: [...(extracted.series || []), lineSeries],
  };
}

/**
 * Defaults for summary reports grouped by 2 row dimensions (e.g. Stage + Owner).
 */
export function getSummaryChartDefaults(
  selectedGroups,
  selectedFields,
  summarizedFields
) {
  if (!Array.isArray(selectedGroups) || selectedGroups.length < 2) return null;

  const measures = buildMeasuresFromSummarized(selectedFields, summarizedFields);
  const xField = selectedGroups[0];
  const stackField = selectedGroups[1];
  const measure = measures[0];
  const measureField = measure?.field_name || 'record_count';

  return {
    chartType: 'stackedColumn',
    selectedCategoryField: xField.field_name,
    stackByField: stackField.field_name,
    selectedValueFields: [measureField],
    matrixMeasureField: measureField,
    matrixLineField: '',
    xAxisLabel: xField.field_label || xField.field_name,
    yAxisLabel: measure
      ? `${measure.field_label || measure.field_name} (${(measure.aggregation || 'sum').toUpperCase()})`
      : 'Record Count',
    summaryStackChart: true,
  };
}

function aggregateSummaryStackCell({
  rows,
  measureField,
  measureMeta,
  selectedFields,
  summarizedFields,
  baseCurrencyCode,
  exchangeRateLookup,
  multiCurrencyEnabled,
}) {
  if (!rows?.length) return 0;
  if (measureField === 'record_count') return rows.length;

  const field =
    (selectedFields || []).find((f) => f.field_name === measureField) ||
    (measureMeta?.field_name === measureField ? measureMeta : null);
  if (!field) return 0;

  const fieldId = field.id || field.field_config_id || field.field_id;
  const sumCfg = summarizedFields?.[fieldId];
  const aggregation =
    sumCfg?.aggregation || measureMeta?.aggregation || 'sum';

  const numeric = getAggregationNumericResult(
    rows,
    field,
    aggregation,
    multiCurrencyEnabled ? baseCurrencyCode : null,
    multiCurrencyEnabled ? exchangeRateLookup : null,
    field?.object_name || measureMeta?.object_name || null
  );
  if (numeric != null) return numeric;

  const base = multiCurrencyEnabled ? baseCurrencyCode : null;
  const display = calculateReportAggregation(
    rows,
    field,
    aggregation,
    base,
    (result, fld, agg) =>
      formatAggregatedDisplayForReport(result, fld, agg, base, (res) =>
        res != null && Number.isFinite(Number(res)) ? String(res) : String(res ?? '')
      ),
    multiCurrencyEnabled ? exchangeRateLookup : null,
    field?.object_name || measureMeta?.object_name || null
  );
  return parseNumeric(display);
}

/**
 * Summary report stacked chart: X = primary row group, stack segments = second row group.
 * Example: group rows by Stage, stack by Owner → each Stage bar split by Owner.
 */
export function extractSummaryStackedChartSeries({
  chart,
  previewData = [],
  selectedGroups = [],
  selectedFields = [],
  summarizedFields = {},
  summarizedData = null,
  matrixDateGranularity = 'month',
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  multiCurrencyEnabled = true,
}) {
  if (!previewData?.length || !selectedGroups?.length) return null;

  const xFieldName = chart.xAxisField || chart.selectedCategoryField;
  const stackFieldName = chart.stackByField || chart.stack_by_field;
  if (!xFieldName || !stackFieldName || xFieldName === stackFieldName) return null;

  const xGroup = selectedGroups.find((g) => g.field_name === xFieldName);
  const stackGroup = selectedGroups.find((g) => g.field_name === stackFieldName);
  if (!xGroup || !stackGroup) return null;

  const measures = buildMeasuresFromSummarized(selectedFields, summarizedFields);
  const measureField = resolveMatrixBarMeasureField(chart, summarizedData, measures);
  if (
    !measures.length &&
    measureField !== 'record_count' &&
    !chart?.yAxisFields?.includes('record_count')
  ) {
    return null;
  }

  const measureMeta = summarizedData?._metadata?.summarized_fields?.[measureField];
  const measure = measures.find(
    (m) => m.field_name === measureField || String(m.id) === String(measureField)
  );
  const dateGroupOptions = buildDateGroupOptions(matrixDateGranularity);

  const xLabelsSet = new Set();
  const stackLabelsSet = new Set();
  previewData.forEach((row) => {
    const { label: xLabel } = getReportGroupBucket(xGroup, row, dateGroupOptions);
    const { label: stackLabel } = getReportGroupBucket(
      stackGroup,
      row,
      dateGroupOptions
    );
    xLabelsSet.add(xLabel || 'N/A');
    stackLabelsSet.add(stackLabel || 'N/A');
  });

  const xFieldInfo =
    summarizedData?._metadata?.group_fields?.[xFieldName] || xGroup;
  const stackFieldInfo =
    summarizedData?._metadata?.group_fields?.[stackFieldName] || stackGroup;

  const labels = sortReportGroupKeys(
    [...xLabelsSet].filter((k) => !isChartGrandTotalCategory(k)),
    xFieldInfo,
    selectedFields
  );

  const stackKeys = sortReportGroupKeys(
    [...stackLabelsSet].filter((k) => !isChartGrandTotalCategory(k)),
    stackFieldInfo,
    selectedFields
  );

  if (!labels.length || !stackKeys.length) return null;

  const stackColors = getCategoryAxisColorsForChart(
    stackKeys,
    stackFieldInfo,
    DEFAULT_COLORS[0],
    0
  );

  const series = stackKeys.map((stackLabel, index) => {
    const color =
      chart.fieldColors?.[stackLabel] ||
      chart.legendColors?.[stackLabel] ||
      (stackColors ? stackColors[index] : DEFAULT_COLORS[index % DEFAULT_COLORS.length]);

    const data = labels.map((xLabel) => {
      const rows = previewData.filter((row) => {
        const xBucket = getReportGroupBucket(xGroup, row, dateGroupOptions);
        const sBucket = getReportGroupBucket(stackGroup, row, dateGroupOptions);
        return (xBucket.label || 'N/A') === xLabel && (sBucket.label || 'N/A') === stackLabel;
      });
      return aggregateSummaryStackCell({
        rows,
        measureField,
        measureMeta,
        selectedFields,
        summarizedFields,
        baseCurrencyCode,
        exchangeRateLookup,
        multiCurrencyEnabled,
      });
    });

    return {
      fieldName: stackLabel,
      name: stackLabel,
      data,
      color,
      perCategoryColors: null,
      seriesType: 'bar',
    };
  });

  const measureLabel =
    measureField === 'record_count'
      ? 'Record Count'
      : measure
        ? `${measure.field_label || measure.field_name} (${(measure.aggregation || 'SUM').toUpperCase()})`
        : measureField;

  return {
    labels,
    series,
    groupFieldInfo: stackFieldInfo,
    selectedCategoryField: xFieldName,
    selectedValueFields: [measureField],
    xAxisLabel:
      chart.xAxisLabel || xFieldInfo?.field_label || xFieldInfo?.field_name || 'Categories',
    yAxisLabel: chart.yAxisLabel || measureLabel,
    stackByField: stackFieldName,
    stackByLabel: stackFieldInfo?.field_label || stackFieldName,
  };
}

export function getMatrixChartDefaults(rowGroups, columnGroups, selectedFields, summarizedFields) {
  const measures = buildMeasuresFromSummarized(selectedFields, summarizedFields);
  if (!rowGroups?.length || !columnGroups?.length) return null;

  const rowField = rowGroups[0];
  const colField = columnGroups[0];
  const measure = measures[0];
  const measureField = measure?.field_name || 'record_count';

  return {
    chartType: 'stackedColumn',
    selectedCategoryField: rowField.field_name,
    stackByField: colField.field_name,
    selectedValueFields: [measureField],
    matrixMeasureField: measureField,
    matrixLineField: '',
    xAxisLabel: rowField.field_label || rowField.field_name,
    yAxisLabel: measure
      ? `${measure.field_label || measure.field_name} (${(measure.aggregation || 'sum').toUpperCase()})`
      : 'Record Count',
    matrixChart: true,
  };
}

/**
 * Build chart series from matrix pivot grid (Salesforce-style).
 */
export function extractMatrixChartSeries({
  chart,
  previewData = [],
  rowGroups = [],
  columnGroups = [],
  selectedFields = [],
  summarizedFields = {},
  summarizedData = null,
  matrixDateGranularity = 'month',
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  multiCurrencyEnabled = true,
}) {
  if (!previewData?.length) return null;

  const rows = rowGroups || [];
  const cols = columnGroups || [];
  if (!rows.length || !cols.length) return null;

  const measures = buildMeasuresFromSummarized(selectedFields, summarizedFields);
  const measureField = resolveMatrixBarMeasureField(chart, summarizedData, measures);
  if (
    !measures.length &&
    measureField !== 'record_count' &&
    !chart?.yAxisFields?.includes('record_count')
  ) {
    return null;
  }

  const pivotOptions = {
    matrixDateGranularity,
    reportFields: selectedFields,
    baseCurrencyCode: multiCurrencyEnabled ? baseCurrencyCode : null,
    exchangeRateLookup: multiCurrencyEnabled ? exchangeRateLookup : null,
  };

  const grid = buildMatrixGrid(previewData, rows, cols, measures, pivotOptions);
  if (!grid?.rowKeys?.length || !grid?.columnLabels?.length) return null;

  const xField = chart.xAxisField || chart.selectedCategoryField;
  const stackField = chart.stackByField;

  if (!xField || !stackField || !measureField) return null;
  if (xField === stackField) return null;

  const xOnRow = rows.some((f) => f.field_name === xField);
  const stackOnRow = rows.some((f) => f.field_name === stackField);
  if (xOnRow === stackOnRow) return null;

  const dataColumns = grid.columnLabels.filter((col) => !isTotalColumn(col));
  if (!dataColumns.length) return null;

  const measure = measures.find(
    (m) => m.field_name === measureField || m.id === measureField
  );
  const mid = measure ? measure.id || measure.field_name : measureField;

  const readCell = (rowKey, colKey) => {
    if (measureField === 'record_count') {
      const countMeasure = measures.find((m) => m.aggregation === 'count');
      const useId = countMeasure
        ? countMeasure.id || countMeasure.field_name
        : '__count__';
      return parseNumeric(grid.cells[rowKey]?.[colKey]?.[useId]);
    }
    return parseNumeric(grid.cells[rowKey]?.[colKey]?.[mid]);
  };

  let labels;
  let seriesItems;

  if (xOnRow && !stackOnRow) {
    labels = grid.rowLabels
      .filter((r) => !isChartGrandTotalCategory(r.label || r.key))
      .map((r) => r.label || r.key);
    const rowKeys = grid.rowLabels.filter(
      (r) => !isChartGrandTotalCategory(r.label || r.key)
    );
    seriesItems = dataColumns.map((col) => ({
      key: col.key,
      name: col.label,
      data: rowKeys.map((row) => readCell(row.key, col.key)),
    }));
  } else {
    labels = dataColumns
      .filter((c) => !isChartGrandTotalCategory(c.label || c.key))
      .map((c) => c.label);
    const colKeys = dataColumns.filter(
      (c) => !isChartGrandTotalCategory(c.label || c.key)
    );
    seriesItems = grid.rowLabels
      .filter((row) => !isChartGrandTotalCategory(row.label || row.key))
      .map((row) => ({
      key: row.key,
      name: row.label || row.key,
      data: colKeys.map((col) => readCell(row.key, col.key)),
    }));
  }

  const stackFieldInfo =
    summarizedData?._metadata?.group_fields?.[stackField] ||
    grid._metadata?.group_fields?.[stackField] ||
    (stackOnRow
      ? rows.find((f) => f.field_name === stackField)
      : cols.find((f) => f.field_name === stackField));

  const xFieldInfo = xOnRow
    ? rows.find((f) => f.field_name === xField)
    : cols.find((f) => f.field_name === xField);

  const showTotalColumn = chartShowsMatrixColumnTotal(chart);

  const categoryTotals = labels.map((_, idx) =>
    seriesItems.reduce((sum, s) => sum + parseNumeric(s.data[idx]), 0)
  );

  const allSeriesItems = showTotalColumn
    ? [
        ...seriesItems,
        {
          key: '__category_total__',
          name: 'Total',
          data: categoryTotals,
          isCategoryTotal: true,
        },
      ]
    : seriesItems;

  const stackSegmentLabels = allSeriesItems
    .filter((s) => !s.isCategoryTotal)
    .map((s) => s.name);
  const stackPicklistColors = getCategoryAxisColorsForChart(
    stackSegmentLabels,
    stackFieldInfo,
    DEFAULT_COLORS[0],
    0
  );
  const stackPicklistColorByName = new Map();
  if (stackPicklistColors) {
    stackSegmentLabels.forEach((name, i) => {
      stackPicklistColorByName.set(name, stackPicklistColors[i]);
    });
  }

  const series = allSeriesItems.map((s, index) => {
    const colorKey = s.name;
    const fieldColor = s.isCategoryTotal
      ? chart.fieldColors?.[MATRIX_CHART_TOTAL_COLOR_KEY] ||
        chart.legendColors?.[MATRIX_CHART_TOTAL_COLOR_KEY] ||
        chart.field_colors?.[MATRIX_CHART_TOTAL_COLOR_KEY] ||
        chart.legend_colors?.[MATRIX_CHART_TOTAL_COLOR_KEY] ||
        MATRIX_CHART_TOTAL_DEFAULT_COLOR
      : chart.fieldColors?.[colorKey] ||
        chart.legendColors?.[colorKey] ||
        stackPicklistColorByName.get(colorKey) ||
        DEFAULT_COLORS[index % DEFAULT_COLORS.length];

    return {
      fieldName: s.key,
      name: s.name,
      data: s.data,
      color: fieldColor,
      perCategoryColors: null,
      seriesType: 'bar',
      isCategoryTotal: Boolean(s.isCategoryTotal),
    };
  });

  const measureLabel =
    measureField === 'record_count'
      ? 'Record Count'
      : measure
        ? `${measure.field_label || measure.field_name} (${(measure.aggregation || 'SUM').toUpperCase()})`
        : measureField;

  return {
    labels,
    series,
    groupFieldInfo: stackFieldInfo,
    selectedCategoryField: xField,
    selectedValueFields: [measureField],
    xAxisLabel:
      chart.xAxisLabel || xFieldInfo?.field_label || xFieldInfo?.field_name || 'Categories',
    yAxisLabel: chart.yAxisLabel || measureLabel,
    stackByField: stackField,
    stackByLabel: stackFieldInfo?.field_label || stackField,
  };
}

/** @deprecated use matrix field names only in legacy charts */
export function isMatrixColumnFieldName(fieldName) {
  return String(fieldName || '').startsWith('__matrix_col__');
}

export default buildSummarizedDataFromMatrix;
